'use client';

import Image from '@/components/Image';
import MagicJump, { type JumpTarget } from '@/components/MagicJump';
import SwiperNavigationButtons from '@/components/SwiperNavigation';
import { useMergedRefs } from '@/hooks/useMergedRefs';
import { useValidExposure } from '@/hooks/useValidExposure';
import logger from '@/utils/client-logger';
import { RightArrowFineOutlined } from '@zeelool/icon';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import type { Swiper as SwiperType } from 'swiper';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import HomeSectionHeading2026 from '../HomeSectionHeading2026/HomeSectionHeading2026';

import type {
  CategoryNavShape2026Data,
  CategoryNavShape2026Item,
  CategoryNavShape2026Props,
  CategoryNavShape2026TrackingParams,
  CategoryNavShape2026ValidExposureParams,
} from './types';

const COMPONENT_ID = 'categoryNavShape';
const DEFAULT_HEADING_COLOR = '#1a1c1e';
const DEFAULT_ITEM_COLOR = 'var(--color-text-primary)';
const WIDE_VISIBLE_SHAPE_COUNT = 6;

const formatText = (value?: unknown) => {
  if (typeof value !== 'string') return '';

  return value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .trim();
};

const buildJumpTarget = (item: CategoryNavShape2026Item): JumpTarget => ({
  jump_type: item.jump_type,
  Jump_target: item.Jump_target ?? undefined,
  ...(item.sku?.shop_sku ? { sku: { shop_sku: item.sku.shop_sku } } : undefined),
  ...(item.product_group?.id || item.product_group?.path
    ? { product_group: item.product_group }
    : undefined),
  ...(item.crowd_recommend?.id ? { crowd_recommend: { id: item.crowd_recommend.id } } : undefined),
});

const buildTrackingParams = ({
  item,
  originalIndex,
  indexData,
  moduleIndex,
}: {
  item: CategoryNavShape2026Item;
  originalIndex: number;
  indexData: CategoryNavShape2026Data;
  moduleIndex: number;
}): CategoryNavShape2026TrackingParams => ({
  module_index: moduleIndex,
  module_name: indexData.component_id || COMPONENT_ID,
  module_id: String(indexData.blocks_advances_id || indexData.id || ''),
  module_title: formatText(indexData.layout_title),
  mkt_index: originalIndex,
  mkt_index_sub: '',
  mkt_jump_type: String(item.jump_type ?? ''),
  mkt_jump_content: String(item.Jump_target ?? ''),
  module_tab_name: '',
  coupon_id_list: '',
  coupon_name_list: '',
  shop_sku: String(item.sku?.shop_sku || ''),
  business_sku: String(item.sku?.business_sku || ''),
  product_sku: String(item.sku?.sku || ''),
  product_id: String(item.sku?.id || ''),
  product_name: String(item.sku?.short_name || ''),
  group_id: String(item.product_group?.id || ''),
  group_name: String(item.product_group?.name || ''),
});

const buildValidExposureParams = (
  trackingParams: CategoryNavShape2026TrackingParams,
): CategoryNavShape2026ValidExposureParams => ({
  module_index: trackingParams.module_index,
  module_id: trackingParams.module_id,
  module_title: trackingParams.module_title || '',
  module_name: trackingParams.module_name,
  module_tab_name: '',
  mkt_index: trackingParams.mkt_index,
  mkt_index_sub: '',
  mkt_jump_type: trackingParams.mkt_jump_type,
  mkt_jump_content: trackingParams.mkt_jump_content,
});

function useTrackingHandlers({
  trackingParams,
  validExposureParams,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: Pick<CategoryNavShape2026Props, 'onTrack' | 'onExpTrack' | 'onValidExpTrack'> & {
  trackingParams: CategoryNavShape2026TrackingParams;
  validExposureParams: CategoryNavShape2026ValidExposureParams;
}) {
  const [exposureRef, inView] = useInView({ threshold: 0.8, triggerOnce: true });

  useEffect(() => {
    if (!inView || !onExpTrack) return;

    try {
      onExpTrack(trackingParams);
    } catch (error) {
      logger.error('2026 PC category shape exposure tracking error:', error);
    }
  }, [inView, onExpTrack, trackingParams]);

  const handleClick = useCallback(() => {
    if (!onTrack) return;

    try {
      onTrack(validExposureParams);
    } catch (error) {
      logger.error('2026 PC category shape click tracking error:', error);
    }
  }, [onTrack, validExposureParams]);

  const { ref: validExposureRef } = useValidExposure({
    callback: () => onValidExpTrack?.(validExposureParams),
  });

  return {
    itemRef: useMergedRefs(exposureRef, validExposureRef),
    handleClick,
  };
}

interface CategoryNavShapeItemProps
  extends Pick<
    CategoryNavShape2026Props,
    'moduleIndex' | 'onTrack' | 'onExpTrack' | 'onValidExpTrack'
  > {
  indexData: CategoryNavShape2026Data;
  item: CategoryNavShape2026Item;
  originalIndex: number;
}

function CategoryNavShapeItem({
  indexData,
  item,
  originalIndex,
  moduleIndex,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: CategoryNavShapeItemProps) {
  const trackingParams = useMemo(
    () => buildTrackingParams({ item, originalIndex, indexData, moduleIndex }),
    [indexData, item, moduleIndex, originalIndex],
  );
  const validExposureParams = buildValidExposureParams(trackingParams);
  const jumpTarget = useMemo(() => buildJumpTarget(item), [item]);
  const { itemRef, handleClick } = useTrackingHandlers({
    trackingParams,
    validExposureParams,
    onTrack,
    onExpTrack,
    onValidExpTrack,
  });
  const title = formatText(item.title);

  return (
    <div ref={itemRef} className="w-full min-w-0">
      <MagicJump
        prefetch
        item={jumpTarget}
        onBeforeJump={handleClick}
        aria-label={title}
        className="group flex w-full flex-col items-center gap-[12px]"
      >
        <span
          className="relative aspect-square w-full overflow-hidden rounded-full bg-[#f7f7f7]"
          aria-hidden
          data-shape-image
        >
          <span
            className="absolute left-[-16.7559%] top-[-30.435%] flex size-[173.0618%] items-center justify-center"
            data-shape-transform
          >
            <Image
              src={item.jump_image || ''}
              alt={`${title} Glasses`}
              width={340}
              height={340}
              loading={moduleIndex < 3 ? 'eager' : 'lazy'}
              priority={moduleIndex < 3}
              sizes="(min-width: 1920px) 340px, (min-width: 1772px) 340px, (min-width: 1600px) calc((100vw - 331px) / 4.25), (min-width: 1440px) calc((100vw - 306px) / 4.25), (min-width: 1280px) calc((100vw - 296px) / 4.25), 190px"
              className="pointer-events-none block size-[81.6498%] max-w-none shrink-0 rotate-[15deg] object-contain mix-blend-multiply"
            />
          </span>
        </span>
        <span
          className="w-full max-w-full whitespace-pre-line break-words text-center font-br text-[14px] leading-[17px] xl:text-[20px] xl:leading-[24px]"
          style={{ color: item.text_color?.trim() || DEFAULT_ITEM_COLOR }}
        >
          {title}
        </span>
      </MagicJump>
    </div>
  );
}

export default function CategoryNavShape2026({
  indexData,
  moduleIndex,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: CategoryNavShape2026Props) {
  const navigationId = useId().replace(/:/g, '');
  const navigationPrefix = `category-nav-shape-${navigationId}`;
  const validItems = (indexData.jump_target || [])
    .map((item, originalIndex) => ({ item, originalIndex, title: formatText(item.title) }))
    .filter(({ item, title }) => Boolean(item.jump_image && title));
  const canNavigate = validItems.length > WIDE_VISIBLE_SHAPE_COUNT;
  const [shouldShowOverflowSlides, setShouldShowOverflowSlides] = useState(false);
  const previousButtonRef = useRef<HTMLButtonElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const bindNavigationElements = useCallback(
    (swiper: SwiperType) => {
      if (!canNavigate) return;

      const previousButton = previousButtonRef.current;
      const nextButton = nextButtonRef.current;
      if (!previousButton || !nextButton) return;

      const navigation = swiper.params.navigation;
      if (navigation && typeof navigation !== 'boolean') {
        navigation.prevEl = previousButton;
        navigation.nextEl = nextButton;
      }

      const originalNavigation = swiper.originalParams.navigation;
      if (originalNavigation && typeof originalNavigation !== 'boolean') {
        originalNavigation.prevEl = previousButton;
        originalNavigation.nextEl = nextButton;
      }
    },
    [canNavigate],
  );
  const syncBeginningState = useCallback((swiper: SwiperType) => {
    setShouldShowOverflowSlides(!swiper.isBeginning);
  }, []);
  const handleMovementStart = useCallback(() => {
    setShouldShowOverflowSlides(true);
  }, []);

  if (validItems.length === 0) return null;

  const headingTitle = formatText(indexData.layout_title);
  const headingSubtitle = formatText(indexData.layout_subtitle);
  const shouldShowHeading =
    Number(indexData.layout_title_status) === 1 && Boolean(headingTitle || headingSubtitle);
  const headingColor = indexData.layout_title_color?.trim() || DEFAULT_HEADING_COLOR;
  return (
    <section
      data-component-id={COMPONENT_ID}
      className="swiper-nav-container relative w-full bg-primary-white"
    >
      {shouldShowHeading ? (
        <HomeSectionHeading2026
          title={headingTitle}
          subtitle={headingSubtitle}
          titleColor={headingColor}
          subtitleColor={
            indexData.layout_subtitle_color || indexData.layout_subtitl_color || undefined
          }
          titleTag="h3"
          subtitleTag="h4"
        />
      ) : null}

      {canNavigate ? (
        <>
          <SwiperNavigationButtons
            navigationPrefix={navigationPrefix}
            prevButtonRef={previousButtonRef}
            nextButtonRef={nextButtonRef}
            prevIcon={
              <RightArrowFineOutlined
                className="swiper-nav-icon size-[20px] rotate-180 [&_path]:stroke-white"
                aria-hidden
              />
            }
            nextIcon={
              <RightArrowFineOutlined
                className="swiper-nav-icon size-[20px] [&_path]:stroke-white"
                aria-hidden
              />
            }
            prevButtonPosition="left-[clamp(24px,calc(-40px_+_6.25vw),40px)] 3xl:left-[56px]"
            nextButtonPosition="right-[clamp(24px,calc(-40px_+_6.25vw),40px)] 3xl:right-[56px]"
            prevButtonClassName="!top-[160px] !z-[400] !size-[32px] !bg-black/30 hover:!scale-100 hover:!bg-black/30 focus-visible:pointer-events-auto focus-visible:opacity-100 xl:!top-[calc(137px_+_clamp(82px,6.25vw,120px))]"
            nextButtonClassName="!top-[160px] !z-[400] !size-[32px] !bg-black/30 hover:!scale-100 hover:!bg-black/30 focus-visible:pointer-events-auto focus-visible:opacity-100 xl:!top-[calc(137px_+_clamp(82px,6.25vw,120px))]"
          />

          <div className="w-full overflow-hidden">
            <div className="relative mx-auto w-full max-w-[1024px] px-[48px] pb-[24px] xl:max-w-[1771px] min-[1920px]:max-w-[1816px]">
              <Swiper
                modules={[Navigation]}
                onBeforeInit={bindNavigationElements}
                onSwiper={syncBeginningState}
                onSliderFirstMove={handleMovementStart}
                onTransitionStart={handleMovementStart}
                onTransitionEnd={syncBeginningState}
                slidesPerView={WIDE_VISIBLE_SHAPE_COUNT}
                slidesPerGroup={1}
                loop={false}
                spaceBetween={24}
                breakpoints={{
                  1280: { slidesPerView: WIDE_VISIBLE_SHAPE_COUNT, spaceBetween: 40 },
                  1440: { slidesPerView: WIDE_VISIBLE_SHAPE_COUNT, spaceBetween: 42 },
                  1600: { slidesPerView: WIDE_VISIBLE_SHAPE_COUNT, spaceBetween: 47 },
                  1920: { slidesPerView: WIDE_VISIBLE_SHAPE_COUNT, spaceBetween: 56 },
                }}
                watchOverflow
                centerInsufficientSlides
                navigation={{
                  prevEl: `.${navigationPrefix}-prev`,
                  nextEl: `.${navigationPrefix}-next`,
                }}
                wrapperTag="ul"
                className={shouldShowOverflowSlides ? '!overflow-visible' : '!overflow-hidden'}
              >
                {validItems.map(({ item, originalIndex }) => (
                  <SwiperSlide
                    key={item.id || `${formatText(item.title)}-${originalIndex}`}
                    tag="li"
                    className="min-w-[134px] shrink-0 [--category-shape-initial-width:calc((min(100vw,1024px)_-_216px)/6)] xl:[--category-shape-initial-width:calc((min(100vw,1771px)_-_296px)/6)] min-[1440px]:[--category-shape-initial-width:calc((min(100vw,1771px)_-_306px)/6)] min-[1600px]:[--category-shape-initial-width:calc((min(100vw,1771px)_-_331px)/6)] min-[1920px]:[--category-shape-initial-width:240px]"
                    style={{ width: 'var(--category-shape-initial-width)' }}
                  >
                    <CategoryNavShapeItem
                      indexData={indexData}
                      item={item}
                      originalIndex={originalIndex}
                      moduleIndex={moduleIndex}
                      onTrack={onTrack}
                      onExpTrack={onExpTrack}
                      onValidExpTrack={onValidExpTrack}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        </>
      ) : (
        <ul className="mx-auto flex w-full max-w-[1024px] flex-wrap justify-center gap-x-[24px] gap-y-[24px] px-[48px] pb-[24px] xl:grid xl:max-w-[1816px] xl:grid-cols-[repeat(6,minmax(164px,240px))] xl:gap-x-[clamp(40px,2.9167vw,56px)]">
          {validItems.map(({ item, originalIndex }) => (
            <li
              key={item.id || `${formatText(item.title)}-${originalIndex}`}
              className="w-[calc((100%_-_120px)/6)] min-w-[134px] max-w-[240px] justify-self-center xl:w-full xl:min-w-[164px]"
            >
              <CategoryNavShapeItem
                indexData={indexData}
                item={item}
                originalIndex={originalIndex}
                moduleIndex={moduleIndex}
                onTrack={onTrack}
                onExpTrack={onExpTrack}
                onValidExpTrack={onValidExpTrack}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
